/**
 * What the draft was written from: the profile check (questions, never a verdict) and the public
 * web facts the recruiter can untick. Read-only except for the fact checkboxes.
 */
import { useState } from 'react';
import type { GapCriterion, OutreachCandidate, Verdict } from '../../types/taOutreach';
import { Badge } from './outreachUi';
import { CARD_FRAME, CARD_HEAD, CARD_TITLE } from './messageBox';

/**
 * Readable at a glance (Sofia, Oct 15 2026): a word, not a symbol, for each verdict; must-haves and
 * nice-to-haves apart; the profile quote on its own quiet line under the criterion.
 */
const VERDICT: Record<Verdict, { text: string; tone: 'green' | 'amber' | 'red' }> = {
  evidenced: { text: 'In profile', tone: 'green' },
  unknown: { text: 'Not in profile', tone: 'amber' },
  contradicted: { text: 'Profile says otherwise', tone: 'red' },
};

function CriteriaGroup({ title, items }: { title: string; items: GapCriterion[] }) {
  if (!items.length) return null;
  const shown = items.filter((c) => c.verdict === 'evidenced').length;
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-semibold text-[#1A1A1A]">
        {title} <span className="font-normal text-[#707070]">· {shown} of {items.length} in profile</span>
      </p>
      <ul className="m-0 list-none divide-y divide-solid divide-[#F0F0F0] rounded-md border p-0 border-solid border-[#E9EAEC] bg-white">
        {items.map((c) => (
          <li key={c.id} className="flex flex-col gap-1 px-3 py-2 sm:flex-row sm:items-start sm:gap-3">
            <span className="shrink-0 sm:w-[132px]">
              <Badge tone={VERDICT[c.verdict].tone}>{VERDICT[c.verdict].text}</Badge>
            </span>
            <span className="min-w-0">
              <span className="block text-[#1A1A1A]">{c.label}</span>
              {c.evidence && <span className="mt-0.5 block text-xs text-[#707070]">“{c.evidence}”</span>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function OutreachEvidence({
  candidate,
  onToggleFact,
  busy,
}: {
  candidate: OutreachCandidate;
  onToggleFact: (factId: string, include: boolean) => void;
  busy: boolean;
}) {
  const gap = candidate.gap;
  const facts = candidate.enrichment?.facts || [];
  const excluded = new Set(candidate.enrichment?.excludedFactIds || []);
  // Open by default; the header keeps the counts when it is folded away.
  const [checkOpen, setCheckOpen] = useState(true);
  const must = gap?.criteria.filter((c) => c.mustHave) || [];
  const nice = gap?.criteria.filter((c) => !c.mustHave) || [];
  const mustShown = must.filter((c) => c.verdict === 'evidenced').length;
  const niceShown = nice.filter((c) => c.verdict === 'evidenced').length;
  return (
    <div className="space-y-3 text-[13px]">
      {gap && (
        <section className={CARD_FRAME}>
          <button
            type="button"
            aria-expanded={checkOpen}
            onClick={() => setCheckOpen((v) => !v)}
            className={`${CARD_HEAD} w-full cursor-pointer border-x-0 border-t-0 text-left hover:bg-[#EEF2F8]`}
          >
            <span className={CARD_TITLE}>
              <span aria-hidden className="mr-1.5 inline-block w-3 text-[#707070]">
                {checkOpen ? '▾' : '▸'}
              </span>
              Profile check
            </span>
            <span className="text-xs text-[#707070]">
              must-haves {mustShown}/{must.length} · nice to have {niceShown}/{nice.length} in profile
            </span>
          </button>
          {checkOpen && (
          <div className="space-y-3 px-4 py-3">
            <CriteriaGroup title="Must-haves" items={must} />
            <CriteriaGroup title="Nice to have" items={nice} />
            {gap.question && (
              <div className="rounded-md border-l-4 border-solid border-y-0 border-r-0 border-[#0072EA] bg-[#F3F8FE] px-3 py-2">
                <p className="text-xs font-semibold text-[#0072EA]">The one question this InMail asks</p>
                <p className="mt-0.5 text-[#1A1A1A]">{gap.question.text}</p>
              </div>
            )}
          </div>
          )}
        </section>
      )}
      {candidate.enrichment && (
        <section>
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#707070]">Public web (never LinkedIn)</h4>
          {candidate.enrichment.status === 'failed' && <p className="text-[#707070]">The web search did not work this time. The draft uses the profile only.</p>}
          {candidate.enrichment.status === 'none' && <p className="text-[#707070]">Nothing found that is clearly this person. The draft uses the profile only.</p>}
          {facts.length > 0 && (
            <>
              <ul className="space-y-1">
                {facts.map((f) => (
                  <li key={f.id} className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={!excluded.has(f.id || '')}
                      disabled={busy}
                      aria-label={`Use: ${f.claim}`}
                      onChange={(e) => onToggleFact(f.id || '', e.target.checked)}
                    />
                    <span>
                      {f.claim}{' '}
                      <a href={f.sourceUrl} target="_blank" rel="noreferrer" className="text-[#0072EA] underline">
                        source ↗
                      </a>
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-[#707070]">
                Check each fact is really this person. Unticked facts are left out the next time you redraft.
              </p>
            </>
          )}
          {(candidate.enrichment.usedFacts || []).length > 0 && (
            <p className="text-xs text-[#707070]">Used in the sent InMail: {candidate.enrichment.usedFacts!.map((f) => f.claim).join(' · ')}</p>
          )}
        </section>
      )}
    </div>
  );
}
